import React, { useEffect, useMemo, useState } from 'react';
import {
	BarChart,
	Bar,
	Cell,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Card, PageHeader, TableSkeleton } from '../../components/ui';
import { EmailWorkflowApi, ReportPeriod, CityReportRow } from '../../services/emailWorkflowApi';
import { formatINR, formatINRDelta, formatMonthLabel } from '../../utils/currencyFormatter';

/**
 * Screen C — Reports (read-only).
 * Single source: GET /email/workflows/report?period=week|month.
 *
 * UX: the screen answers "are we keeping up?" not just "what are the numbers".
 * A plain-English lead reads the latest period for you; the chart shows the
 * money moving (latest period highlighted); the table adds per-period deltas
 * so it earns its place instead of restating the chart. Fully read-only.
 */

type Period = 'week' | 'month';

// Month periods come back as "2026-06"; week periods as some other label.
// Only "YYYY-MM" gets the nice "Jun 2026" label — anything else is shown verbatim.
const labelForPeriod = (period: string): string =>
	/^\d{4}-\d{2}$/.test(period) ? formatMonthLabel(period) : period;

// Rows arrive chronological-oldest-first from the API? Not guaranteed — sort by
// the period string so "latest" and the deltas are always correct. String sort
// is safe for "YYYY-MM" and for ISO week labels ("2026-W23").
const sortByPeriod = (rows: ReportPeriod[]): ReportPeriod[] =>
	[...rows].sort((a, b) => a.period.localeCompare(b.period));

type Mode = 'month' | 'week' | 'city';

export const EmailWorkflowReports: React.FC = () => {
	const [mode, setMode] = useState<Mode>('month');
	const period: Period = mode === 'week' ? 'week' : 'month';
	const [rows, setRows] = useState<ReportPeriod[]>([]);
	const [cities, setCities] = useState<CityReportRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		const fetchIt =
			mode === 'city'
				? EmailWorkflowApi.getCityReport().then(r => {
						if (!cancelled) setCities(Array.isArray(r?.cities) ? r.cities : []);
					})
				: EmailWorkflowApi.getReport(period).then(r => {
						if (!cancelled) setRows(sortByPeriod(Array.isArray(r?.periods) ? r.periods : []));
					});
		fetchIt
			.catch((err: unknown) => {
				if (cancelled) return;
				setError((err as { message?: string })?.message || 'Could not load the report.');
				setRows([]);
				setCities([]);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [mode, period, reloadKey]);

	const unit = period === 'month' ? 'month' : 'week';

	// City mode: sort biggest-spend first; label unattributed bucket.
	const cityData = useMemo(
		() =>
			[...cities]
				.sort((a, b) => b.amount - a.amount)
				.map(c => ({ label: c.city || 'Unattributed', processed: c.processed, amount: c.amount })),
		[cities]
	);
	const cityTotal = useMemo(
		() => cities.reduce((s, c) => s + (c.amount || 0), 0),
		[cities]
	);

	// Latest period + its change vs the one before — the "answer" the page leads with.
	const latest = rows[rows.length - 1];
	const previous = rows[rows.length - 2];
	const amountDelta = latest && previous ? latest.amount - previous.amount : null;

	// oldest-first chart data; flag the latest bar so it reads as "now".
	const chartData = useMemo(
		() =>
			rows.map((r, i) => ({
				label: labelForPeriod(r.period),
				processed: r.processed,
				amount: r.amount,
				isLatest: i === rows.length - 1,
			})),
		[rows]
	);

	return (
		<div className='p-6'>
			<PageHeader
				title='Approval Reports'
				totalItems={rows.length}
				itemType={period === 'month' ? 'months' : 'weeks'}
				icon='📊'
			/>

			<div
				className='mb-6 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5'
				role='group'
				aria-label='Report mode'
			>
				{([
					{ id: 'month', label: 'Monthly' },
					{ id: 'week', label: 'Weekly' },
					{ id: 'city', label: 'By city' },
				] as const).map(m => (
					<button
						key={m.id}
						type='button'
						onClick={() => setMode(m.id)}
						aria-pressed={mode === m.id}
						className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
							mode === m.id
								? 'bg-white text-primary shadow-sm'
								: 'text-gray-500 hover:text-gray-700'
						}`}
					>
						{m.label}
					</button>
				))}
			</div>

			{/* ── City mode ─────────────────────────────────────────── */}
			{mode === 'city' && !loading && !error && (
				cities.length === 0 ? (
					<Card className='text-center py-12'>
						<div className='text-4xl mb-3'>🗺️</div>
						<p className='text-gray-800 font-medium'>No city breakdown yet</p>
						<p className='text-sm text-gray-500 mt-1 max-w-md mx-auto'>
							Once payments are processed and attributed to a city, they show up here.
						</p>
					</Card>
				) : (
					<>
						<Card className='mb-6'>
							<h2 className='text-sm font-semibold text-gray-700 mb-4'>Amount processed by city</h2>
							<ResponsiveContainer width='100%' height={300}>
								<BarChart data={cityData} margin={{ top: 20, right: 10, left: 10, bottom: 10 }}>
									<CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
									<XAxis dataKey='label' stroke='#6b7280' fontSize={12} tick={{ fill: '#6b7280' }} />
									<YAxis
										stroke='#6b7280'
										fontSize={12}
										tick={{ fill: '#6b7280' }}
										tickFormatter={(v: number) => formatINR(v, { compact: true })}
										width={70}
									/>
									<Tooltip
										cursor={{ fill: 'rgba(0,167,111,0.06)' }}
										contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '13px' }}
										formatter={(value: number, name: string) =>
											name === 'amount' ? [formatINR(value), 'Amount'] : [value, 'Processed']
										}
									/>
									<Bar dataKey='amount' fill='#00a76f' radius={[4, 4, 0, 0]} />
								</BarChart>
							</ResponsiveContainer>
						</Card>
						<Card>
							<table className='w-full text-[13px]'>
								<thead>
									<tr className='border-b border-gray-200 text-left text-gray-500'>
										<th className='py-2 pr-4 font-medium'>City</th>
										<th className='py-2 pr-4 font-medium text-right'>Processed</th>
										<th className='py-2 font-medium text-right'>Amount</th>
									</tr>
								</thead>
								<tbody>
									{cityData.map(c => (
										<tr key={c.label} className='border-b border-gray-100 last:border-0'>
											<td className='py-2 pr-4 text-gray-900'>{c.label}</td>
											<td className='py-2 pr-4 text-right tabular-nums text-gray-900'>{c.processed}</td>
											<td className='py-2 text-right tabular-nums text-gray-900'>{formatINR(c.amount)}</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr className='font-semibold text-gray-900'>
										<td className='py-2 pr-4'>Total</td>
										<td className='py-2 pr-4 text-right tabular-nums'>
											{cities.reduce((s, c) => s + (c.processed || 0), 0)}
										</td>
										<td className='py-2 text-right tabular-nums'>{formatINR(cityTotal)}</td>
									</tr>
								</tfoot>
							</table>
						</Card>
					</>
				)
			)}

			{loading && <TableSkeleton />}

			{!loading && error && (
				<Card className='text-center py-10'>
					<p className='text-red-600 font-medium'>{error}</p>
					<button
						type='button'
						onClick={() => setReloadKey(k => k + 1)}
						className='mt-3 text-sm text-primary underline'
					>
						Try again
					</button>
				</Card>
			)}

			{mode !== 'city' && !loading && !error && rows.length === 0 && (
				<Card className='text-center py-12'>
					<div className='text-4xl mb-3'>📭</div>
					<p className='text-gray-800 font-medium'>Nothing processed yet</p>
					<p className='text-sm text-gray-500 mt-1 max-w-md mx-auto'>
						Approvals show up here once payments are marked paid or done. Check back after this{' '}
						{unit}&rsquo;s first one clears.
					</p>
				</Card>
			)}

			{mode !== 'city' && !loading && !error && rows.length > 0 && latest && (
				<>
					{/* Lead: read the latest period in plain English. The signature element. */}
					<Card className='mb-6 border-l-4 border-l-primary'>
						<p className='text-lg text-gray-900 leading-snug'>
							This {unit} you processed{' '}
							<span className='font-semibold text-primary'>{formatINR(latest.amount)}</span> across{' '}
							<span className='font-semibold'>
								{latest.processed} {latest.processed === 1 ? 'approval' : 'approvals'}
							</span>
							.
						</p>
						{amountDelta !== null ? (
							<p className='mt-1 flex items-center gap-1 text-sm'>
								<DeltaBadge value={amountDelta} />
								<span className='text-gray-500'>vs last {unit}</span>
							</p>
						) : (
							<p className='mt-1 text-sm text-gray-400'>First {unit} on record.</p>
						)}
					</Card>

					<Card className='mb-6'>
						<h2 className='text-sm font-semibold text-gray-700 mb-4'>
							Amount processed per {unit}
						</h2>
						<ResponsiveContainer width='100%' height={300}>
							<BarChart data={chartData} margin={{ top: 20, right: 10, left: 10, bottom: 10 }}>
								<CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' vertical={false} />
								<XAxis dataKey='label' stroke='#6b7280' fontSize={12} tick={{ fill: '#6b7280' }} />
								<YAxis
									stroke='#6b7280'
									fontSize={12}
									tick={{ fill: '#6b7280' }}
									tickFormatter={(v: number) => formatINR(v, { compact: true })}
									width={70}
								/>
								<Tooltip
									cursor={{ fill: 'rgba(0,167,111,0.06)' }}
									contentStyle={{
										backgroundColor: 'white',
										border: '1px solid #e5e7eb',
										borderRadius: '6px',
										fontSize: '13px',
									}}
									formatter={(value: number, name: string) =>
										name === 'amount'
											? [formatINR(value), 'Amount']
											: [value, 'Processed']
									}
								/>
								{/* count is invisible on the axis but present so the tooltip can show it */}
								<Bar dataKey='amount' radius={[4, 4, 0, 0]}>
									{chartData.map(d => (
										<Cell key={d.label} fill={d.isLatest ? '#00a76f' : '#c7d2cd'} />
									))}
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					</Card>

					<Card>
						<table className='w-full text-[13px]'>
							<thead>
								<tr className='border-b border-gray-200 text-left text-gray-500'>
									<th className='py-2 pr-4 font-medium'>Period</th>
									<th className='py-2 pr-4 font-medium text-right'>Processed</th>
									<th className='py-2 pr-4 font-medium text-right'>Amount</th>
									<th className='py-2 font-medium text-right'>Change</th>
								</tr>
							</thead>
							<tbody>
								{/* newest first in the table — matches how people scan a report */}
								{rows
									.map((r, i) => ({ r, prev: rows[i - 1] }))
									.reverse()
									.map(({ r, prev }) => (
										<tr key={r.period} className='border-b border-gray-100 last:border-0'>
											<td className='py-2 pr-4 text-gray-900'>{labelForPeriod(r.period)}</td>
											<td className='py-2 pr-4 text-right tabular-nums text-gray-900'>
												{r.processed}
											</td>
											<td className='py-2 pr-4 text-right tabular-nums text-gray-900'>
												{formatINR(r.amount)}
											</td>
											<td className='py-2 text-right'>
												{prev ? (
													<DeltaBadge value={r.amount - prev.amount} align='right' />
												) : (
													<span className='text-gray-300'>—</span>
												)}
											</td>
										</tr>
									))}
							</tbody>
						</table>
					</Card>
				</>
			)}
		</div>
	);
};

/** Signed ₹ delta with an up/down/flat arrow. Green up, red down — more money moved is good. */
const DeltaBadge: React.FC<{ value: number; align?: 'left' | 'right' }> = ({
	value,
	align = 'left',
}) => {
	const up = value > 0;
	const flat = value === 0;
	const color = flat ? 'text-gray-400' : up ? 'text-primary' : 'text-red-500';
	const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
	return (
		<span
			className={`inline-flex items-center gap-0.5 tabular-nums font-medium ${color} ${
				align === 'right' ? 'justify-end' : ''
			}`}
		>
			<Icon className='h-3.5 w-3.5' />
			{formatINRDelta(value)}
		</span>
	);
};

export default EmailWorkflowReports;
